// frontend/src/services/api/config.js
import axios from "axios";
import { store } from "../../store";
import { refreshToken, logout } from "../../store/slices/authSlice";
let isSessionExpiredHandlerActive = false;

const api = axios.create({
  baseURL:
    process.env.REACT_APP_API_URL || "https://resellerstrongesim.cloud/api",
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000, // 10 seconds timeout
  withCredentials: true, // Enable credentials
});

// Token refresh state management
let isRefreshing = false;
let failedQueue = [];

export const handleSessionExpired = () => {
  if (isSessionExpiredHandlerActive) return;

  isSessionExpiredHandlerActive = true;

  // Dispatch to auth slice to show modal
  store.dispatch({
    type: "auth/sessionExpired",
    payload: "Your session has expired. Please login again.",
  });

  // Reset after a delay
  setTimeout(() => {
    isSessionExpiredHandlerActive = false;
  }, 10000);
};

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Enhanced debug logging with timestamp
const logDebug = (message, data = {}) => {
  if (process.env.NODE_ENV === "development") {
    // console.log(`[API ${timestamp}] ${message}`, {
    //   ...data,
    //   timestamp,
    // });
  }
};

// Token management
const getStoredTokens = () => ({
  accessToken: localStorage.getItem("accessToken"),
  refreshToken: localStorage.getItem("refreshToken"),
});

const updateStoredTokens = (accessToken, refreshToken) => {
  if (accessToken) {
    localStorage.setItem("accessToken", accessToken);
    api.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
  }
  if (refreshToken) {
    localStorage.setItem("refreshToken", refreshToken);
  }
};

const clearStoredTokens = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  delete api.defaults.headers.common["Authorization"];
};

// Request interceptor with enhanced error handling
api.interceptors.request.use(
  (config) => {
    const { accessToken } = getStoredTokens();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
      logDebug("Request configured with token", {
        url: config.url,
        method: config.method,
        hasToken: !!accessToken,
      });
    }
    return config;
  },
  (error) => {
    logDebug("Request configuration failed", {
      error: error.message,
      stack: error.stack,
    });
    return Promise.reject(error);
  }
);

// Add request/response timing
api.interceptors.request.use((config) => {
  config.metadata = { startTime: new Date() };
  return config;
});

api.interceptors.response.use(
  (response) => {
    const endTime = new Date();
    const duration = endTime - response.config.metadata.startTime;
    logDebug("Request completed", {
      url: response.config.url,
      duration: `${duration}ms`,
      status: response.status,
    });
    return response;
  },
  (error) => {
    const endTime = new Date();
    // Add this null check to prevent the error
    const duration = error.config?.metadata?.startTime
      ? endTime - error.config.metadata.startTime
      : 0;

    logDebug("Request failed", {
      url: error.config?.url || "unknown",
      duration: `${duration}ms`,
      error: error.message,
    });
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Don't retry validation errors (400 status code)
    if (error.response?.status === 400) {
      logDebug("Validation error, not retrying", {
        url: originalRequest.url,
        error: error.response?.data?.message,
      });
      return Promise.reject(error);
    }

    if (error.response?.status === 429 && !originalRequest._retry) {
      originalRequest._retry = true;
      const retryAfter = parseInt(error.response.headers["retry-after"]) || 5;

      logDebug("Rate limited, retrying after delay", {
        retryAfter,
        url: originalRequest.url,
      });

      await new Promise((resolve) => setTimeout(resolve, retryAfter * 1000));
      return api(originalRequest);
    }

    return Promise.reject(error);
  }
);
// Response interceptor with enhanced token refresh logic
api.interceptors.response.use(
  (response) => {
    logDebug("Response received", {
      url: response.config.url,
      status: response.status,
      method: response.config.method,
    });
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Only process 401 errors in this interceptor
    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }

    logDebug("Response error detected", {
      url: originalRequest?.url,
      status: error.response?.status,
      error: error.message,
    });

    // In the isTokenExpired check, consider adding more token expiration conditions
    const isTokenExpired =
      error.response?.data?.message === "Token expired" ||
      error.response?.data?.message === "jwt expired" ||
      error.response?.data?.message === "Invalid token" ||
      error.response?.data?.message?.includes("Password has been changed");

    if (isTokenExpired && !originalRequest._retry) {
      handleSessionExpired();
      return Promise.reject(error);
    }

    // Queue management for concurrent requests during token refresh
    if (isRefreshing) {
      logDebug("Token refresh in progress, queueing request");
      try {
        const newToken = await new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        });
        originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (error) {
        return Promise.reject(error);
      }
    }

    // Initialize token refresh process
    try {
      originalRequest._retry = true;
      isRefreshing = true;

      const { refreshToken: currentRefreshToken } = getStoredTokens();

      if (!currentRefreshToken) {
        throw new Error("No refresh token available");
      }

      logDebug("Starting token refresh");
      const result = await store
        .dispatch(refreshToken(currentRefreshToken))
        .unwrap();

      updateStoredTokens(result.accessToken, result.refreshToken);
      processQueue(null, result.accessToken);

      logDebug("Token refresh successful", {
        tokenReceived: !!result.accessToken,
      });

      originalRequest.headers["Authorization"] = `Bearer ${result.accessToken}`;
      return api(originalRequest);
    } catch (refreshError) {
      logDebug("Token refresh failed", {
        error: refreshError.message,
        stack: refreshError.stack,
      });

      processQueue(refreshError, null);
      clearStoredTokens();
      store.dispatch(logout());

      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

// Enhanced error handler
api.handleError = (error) => {
  const errorDetails = {
    message: error.message,
    code: error.response?.status,
    data: error.response?.data,
  };

  logDebug("API error handled", errorDetails);

  if (error.response) {
    return {
      message: error.response.data.message || "Server error occurred",
      status: error.response.status,
      data: error.response.data,
    };
  } else if (error.request) {
    return {
      message: "No response from server",
      status: 0,
      data: null,
    };
  }
  return {
    message: "Request setup failed",
    status: -1,
    data: null,
  };
};

export default api;
